import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import { BrandLogo } from "@/components/BrandLogo";
import { CommandPalette } from "@/components/CommandPalette";
import { AdminPresenceChip } from "@/components/AdminPresenceChip";
import { FulfilmentOutstandingBadge } from "@/components/FulfilmentOutstandingBadge";
import { ServiceM8ReviewBadge } from "@/components/ServiceM8ReviewBadge";
import { ServiceM8AlertsBadge } from "@/components/ServiceM8AlertsBadge";
import { getNavStructure } from "@/lib/nav";
import { usePresenceHeartbeat } from "@/lib/usePresenceHeartbeat";

// Re-export so older imports (`from "@/components/PortalShell"`) keep working
// while we migrate consumers over to `@/lib/nav`.
export { getNavStructure };

export function PortalShell({ children, title, subtitle, showNav = true, wide = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Feb 2026 — every logged-in tab pings the presence heartbeat so
  // the admin "● N online" chip can show who's actively using the app.
  usePresenceHeartbeat();

  // Auto-close the drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Prevent body scroll while drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  const nav = showNav && user ? getNavStructure(user.role, user) : { primary: [], settings: [] };
  // Flatten groups for the mobile drawer and for "any link active" detection
  const flattenedPrimary = nav.primary.flatMap((l) => l.children ? l.children : [l]);
  const links = [...flattenedPrimary, ...nav.settings];
  const isActive = (to) => to && (location.pathname === to || (to !== "/" && location.pathname.startsWith(to + "/")));
  const settingsActive = nav.settings.some((l) => isActive(l.to));
  const groupActive = (group) => (group.children || []).some((c) => isActive(c.to));
  const [openGroup, setOpenGroup] = useState(null);  // which group dropdown is open
  // Oct 2026 — the group dropdown panel is rendered `position: fixed`
  // with JS-computed coordinates (not CSS `absolute`) so it's never
  // clipped by an ancestor with `overflow: hidden`/`auto` and always
  // renders directly below whichever row its trigger button wrapped
  // onto (the nav wraps onto multiple rows rather than scrolling).
  const [menuPos, setMenuPos] = useState(null);
  const openGroupDropdown = (label, btnEl) => {
    if (openGroup === label) { setOpenGroup(null); return; }
    const r = btnEl.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, left: r.left });
    setOpenGroup(label);
  };
  // Resizing the window or scrolling the page while a dropdown is open
  // would leave the fixed-positioned panel drifting away from its
  // trigger button — just close it.
  useEffect(() => {
    if (!openGroup) return;
    const close = () => setOpenGroup(null);
    window.addEventListener("scroll", close, { passive: true });
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close);
      window.removeEventListener("resize", close);
    };
  }, [openGroup]);
  // Click-outside to close group dropdowns
  useEffect(() => {
    if (!openGroup) return;
    const handler = (e) => {
      if (!e.target.closest("[data-nav-group]")) setOpenGroup(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpenGroup(null); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", onKey);
    };
  }, [openGroup]);
  // Close on route change
  useEffect(() => { setOpenGroup(null); }, [location.pathname]);

  // Admins get cyan styling; members get neutral slate.
  const isStaff = user && (user.role === "admin" || user.role === "lab_technician");

  // Role label is admin/lab tech/member-friendly. Company name (from
  // members / trade partners) wins when present.
  const ROLE_LABELS = { admin: "Admin", lab_technician: "Lab Tech", member: "Member", customer: "Customer" };
  const roleLabel = user ? (user.company_name || ROLE_LABELS[user.role] || user.role) : "";

  // Desktop "Settings" dropdown — closes on outside-click or Escape.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  useEffect(() => {
    if (!settingsOpen) return;
    const onDoc = (e) => { if (settingsRef.current && !settingsRef.current.contains(e.target)) setSettingsOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setSettingsOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [settingsOpen]);
  useEffect(() => { setSettingsOpen(false); }, [location.pathname]);

  // Mobile drawer: track which group sections are expanded. Default: expand the
  // group containing the current route so the user lands on a meaningful set.
  const [expandedDrawerGroups, setExpandedDrawerGroups] = useState({});
  useEffect(() => {
    if (!drawerOpen) return;
    const initial = {};
    for (const g of nav.primary) {
      if (g.children && groupActive(g)) initial[g.label] = true;
    }
    if (nav.settings.length && nav.settings.some((l) => isActive(l.to))) initial.__settings = true;
    setExpandedDrawerGroups(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);
  const toggleDrawerGroup = (key) => setExpandedDrawerGroups((s) => ({ ...s, [key]: !s[key] }));

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center gap-3 sm:gap-4">
          {/* Hamburger — visible whenever there are nav links AND the inline
              bar can't fit them all (<= md). */}
          {links.length > 0 && (
            <button
              data-testid="portal-nav-hamburger"
              type="button"
              aria-label="Open navigation menu"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden p-2 -ml-1 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors flex-shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}

          <div className="flex-shrink-0">
            <BrandLogo to="/" size="sm" />
          </div>

          {/* Desktop inline nav — wraps onto a second row if the primary
              groups + Settings + the badge/search/profile cluster don't
              fit on one line at the current width (restored Oct 2026
              per explicit user preference over a horizontal-scroll
              nav). The "Settings" dropdown is a sibling (not nested)
              so it wraps independently of the primary group list. */}
          {(nav.primary.length > 0 || nav.settings.length > 0) && (
            <div className="hidden lg:flex flex-1 min-w-0 items-center gap-1 flex-wrap">
              <nav
                data-testid="portal-nav-desktop"
                className="flex flex-1 min-w-0 items-center gap-0.5 flex-wrap"
              >
                {nav.primary.map((l) => {
                  const base = "whitespace-nowrap px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors flex-shrink-0";
                  // ----- Dropdown group -----
                  if (l.children && l.children.length > 0) {
                    const active = groupActive(l);
                    const tone = isStaff
                      ? (active ? "text-cyan-900 bg-cyan-50" : "text-cyan-700 hover:text-cyan-900 hover:bg-cyan-50")
                      : (active ? "text-slate-900 bg-slate-100" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100");
                    const isOpen = openGroup === l.label;
                    return (
                      <div key={l.label} data-nav-group={l.label} className="relative flex-shrink-0">
                        <button
                          type="button"
                          data-testid={l.testId}
                          aria-haspopup="menu"
                          aria-expanded={isOpen}
                          onClick={(e) => openGroupDropdown(l.label, e.currentTarget)}
                          className={`${base} ${tone} inline-flex items-center gap-1`}
                        >
                          {l.label}
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                        {isOpen && menuPos && (
                          <div
                            role="menu"
                            data-testid={`${l.testId}-menu`}
                            style={{ position: "fixed", top: menuPos.top, left: menuPos.left }}
                            className="w-60 max-h-[72vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1.5 z-50"
                          >
                            {l.children.map((child) => child.heading ? (
                              <div
                                key={`h-${child.heading}`}
                                className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"
                              >
                                {child.heading}
                              </div>
                            ) : (
                              <Link
                                key={child.to}
                                to={child.to}
                                role="menuitem"
                                data-testid={child.testId}
                                onClick={() => setOpenGroup(null)}
                                className={`block px-3 py-1.5 text-[13px] font-medium transition-colors ${
                                  isActive(child.to) ? "text-cyan-900 bg-cyan-50" : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                                }`}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  // ----- Flat link -----
                  const active = isActive(l.to);
                  const tone = isStaff
                    ? (active ? "text-cyan-900 bg-cyan-50" : "text-cyan-700 hover:text-cyan-900 hover:bg-cyan-50")
                    : (active ? "text-slate-900 bg-slate-100" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100");
                  return (
                    <Link key={l.to} to={l.to} data-testid={l.testId} className={`${base} ${tone}`}>
                      {l.label}
                    </Link>
                  );
                })}
              </nav>
              {nav.settings.length > 0 && (
                <div ref={settingsRef} className="relative flex-shrink-0">
                  <button
                    data-testid="portal-nav-settings-trigger"
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={settingsOpen}
                    onClick={() => setSettingsOpen((v) => !v)}
                    className={`inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors ${
                      isStaff
                        ? (settingsActive ? "text-cyan-900 bg-cyan-50" : "text-cyan-700 hover:text-cyan-900 hover:bg-cyan-50")
                        : (settingsActive ? "text-slate-900 bg-slate-100" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100")
                    }`}
                  >
                    Settings
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${settingsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {settingsOpen && (
                    <div
                      data-testid="portal-nav-settings-menu"
                      role="menu"
                      className="absolute right-0 top-full mt-1 w-60 max-h-[72vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1.5 z-50"
                    >
                      {nav.settings.map((l) => {
                        if (l.heading) return (
                          <div
                            key={`h-${l.heading}`}
                            className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"
                          >
                            {l.heading}
                          </div>
                        );
                        const active = isActive(l.to);
                        return (
                          <Link
                            key={l.to}
                            to={l.to}
                            role="menuitem"
                            data-testid={l.testId}
                            onClick={() => setSettingsOpen(false)}
                            className={`block px-3 py-1.5 text-[13px] font-medium transition-colors ${
                              active
                                ? "text-cyan-900 bg-cyan-50"
                                : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                            }`}
                          >
                            {l.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Spacer when no nav so the right cluster floats to the end */}
          {nav.primary.length === 0 && nav.settings.length === 0 && <div className="flex-1" />}

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-auto lg:ml-0">
            {user && user.role === "admin" && <FulfilmentOutstandingBadge />}
            {user && user.role === "admin" && <ServiceM8ReviewBadge />}
            {user && user.role === "admin" && <ServiceM8AlertsBadge />}
            {user && user.role === "admin" && <AdminPresenceChip />}
            {user && <CommandPalette />}
            {user ? (
              <>
                <div className="hidden xl:flex flex-col items-end leading-tight max-w-[180px]">
                  <span className="text-sm font-semibold text-slate-900 truncate w-full text-right">{user.full_name}</span>
                  <span data-testid="portal-user-role-label" className="text-[10px] uppercase tracking-wider text-slate-500 truncate w-full text-right">{roleLabel}</span>
                </div>
                <button
                  data-testid={AUTH.logoutBtn}
                  onClick={async () => { await logout(); window.location.assign("/"); }}
                  className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                  title="Log out"
                  aria-label="Log out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Link to="/" className="text-sm font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1.5 whitespace-nowrap">
                <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Back to site</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Mobile / tablet slide-out drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="Close navigation menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
          />
          <aside
            data-testid="portal-nav-drawer"
            className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-200">
              <BrandLogo to="/" size="sm" />
              <button
                data-testid="portal-nav-drawer-close"
                type="button"
                aria-label="Close navigation menu"
                onClick={() => setDrawerOpen(false)}
                className="p-2 -mr-1 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {user && (
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                <div className="text-sm font-semibold text-slate-900 truncate">{user.full_name}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{roleLabel}</div>
              </div>
            )}
            <nav className="flex-1 overflow-y-auto py-2">
              {nav.primary.map((l) => {
                // Render groups as a collapsible labelled section
                if (l.children && l.children.length > 0) {
                  const expanded = !!expandedDrawerGroups[l.label];
                  const active = groupActive(l);
                  return (
                    <div key={l.label} className="mt-1">
                      <button
                        type="button"
                        data-testid={l.testId ? `${l.testId}-drawer-toggle` : undefined}
                        aria-expanded={expanded}
                        onClick={() => toggleDrawerGroup(l.label)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold transition-colors ${
                          active ? "text-cyan-900 bg-cyan-50" : "text-slate-800 hover:bg-slate-100"
                        }`}
                      >
                        <span>{l.label}</span>
                        <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
                      </button>
                      {expanded && (
                        <div data-testid={l.testId ? `${l.testId}-drawer-panel` : undefined} className="bg-slate-50/60">
                          {l.children.map((child) => {
                            if (child.heading) return (
                              <div key={`h-${child.heading}`} className="pl-8 pr-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                {child.heading}
                              </div>
                            );
                            const a = isActive(child.to);
                            const tone = isStaff
                              ? (a ? "text-cyan-900 bg-cyan-50 border-l-2 border-cyan-600" : "text-cyan-800 hover:bg-cyan-50 border-l-2 border-transparent")
                              : (a ? "text-slate-900 bg-slate-100 border-l-2 border-slate-700" : "text-slate-700 hover:bg-slate-100 border-l-2 border-transparent");
                            return (
                              <Link
                                key={child.to}
                                to={child.to}
                                data-testid={child.testId ? `${child.testId}-drawer` : undefined}
                                onClick={() => setDrawerOpen(false)}
                                className={`block pl-8 pr-4 py-2.5 text-sm font-medium ${tone}`}
                              >
                                {child.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                const active = isActive(l.to);
                const tone = isStaff
                  ? (active ? "text-cyan-900 bg-cyan-50 border-l-2 border-cyan-600" : "text-cyan-800 hover:bg-cyan-50 border-l-2 border-transparent")
                  : (active ? "text-slate-900 bg-slate-100 border-l-2 border-slate-700" : "text-slate-700 hover:bg-slate-100 border-l-2 border-transparent");
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={l.testId ? `${l.testId}-drawer` : undefined}
                    onClick={() => setDrawerOpen(false)}
                    className={`block px-4 py-2.5 text-sm font-medium ${tone}`}
                  >
                    {l.label}
                  </Link>
                );
              })}
              {nav.settings.length > 0 && (
                <div className="mt-1">
                  <button
                    type="button"
                    data-testid="portal-nav-settings-drawer-toggle"
                    aria-expanded={!!expandedDrawerGroups.__settings}
                    onClick={() => toggleDrawerGroup("__settings")}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold transition-colors ${
                      settingsActive ? "text-cyan-900 bg-cyan-50" : "text-slate-800 hover:bg-slate-100"
                    }`}
                  >
                    <span>Settings</span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${expandedDrawerGroups.__settings ? "rotate-90" : ""}`} />
                  </button>
                  {expandedDrawerGroups.__settings && (
                    <div className="bg-slate-50/60">
                      {nav.settings.map((l) => {
                        if (l.heading) return (
                          <div key={`h-${l.heading}`} className="pl-8 pr-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                            {l.heading}
                          </div>
                        );
                        const active = isActive(l.to);
                        const tone = active
                          ? "text-cyan-900 bg-cyan-50 border-l-2 border-cyan-600"
                          : "text-slate-700 hover:bg-slate-100 border-l-2 border-transparent";
                        return (
                          <Link
                            key={l.to}
                            to={l.to}
                            data-testid={l.testId ? `${l.testId}-drawer` : undefined}
                            onClick={() => setDrawerOpen(false)}
                            className={`block pl-8 pr-4 py-2.5 text-sm font-medium ${tone}`}
                          >
                            {l.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </nav>
            {user && (
              <div className="border-t border-slate-200 p-3">
                <button
                  onClick={async () => { setDrawerOpen(false); await logout(); window.location.assign("/"); }}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <LogOut className="w-4 h-4" /> Log out
                </button>
              </div>
            )}
          </aside>
        </div>
      )}

      <main className={`${wide ? "max-w-[1600px]" : "max-w-7xl"} mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12`}>
        {(title || subtitle) && (
          <div className="mb-8">
            {subtitle && <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600 mb-2">{subtitle}</div>}
            {title && <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 break-words">{title}</h1>}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
